import { Prose } from '../../components/Prose'
import { useState } from 'react'
import { useDeleteMeasurement, useMeasurements, useSaveMeasurement } from '../../api/hooks'
import { todayIso, trDate } from '../../lib/format'
import {
  BMI_LABEL, BODY_FAT_LABEL, RFM_UNCERTAINTY, WAIST_NOISE_CM, bmi, bmiCategory,
  bodyFatBand, leanMassKg, projectBodyFat, rfm, rfmRange, trainingBenefit,
  waistChangeIsReal, waistTarget, whtr, whtrBand, WHTR_LABEL, type Sex,
} from '../../lib/body'
import type { Profile } from '../../lib/metrics'

const n1 = (v: number) => v.toLocaleString('tr-TR', { maximumFractionDigits: 1, minimumFractionDigits: 1 })

export function BodyCard({ profile, weightKg }: { profile: Profile; weightKg: number }) {
  const rows = useMeasurements()
  const save = useSaveMeasurement()
  const del = useDeleteMeasurement()
  const [form, setForm] = useState<{ waist: string; hip: string }>({ waist: '', hip: '' })

  // Boş string'e karşı savunma: eski kayıtlarda Sex kolonu '' olabilir ve
  // `?? 'male'` bunu yakalamaz — '' null değil.
  const sex: Sex = profile.sex === 'female' ? 'female' : 'male'
  const list = rows.data ?? []
  const latest = list[list.length - 1]
  const previous = list[list.length - 2]

  const b = bmi(weightKg, profile.heightCm)
  const cat = bmiCategory(b)

  const submit = () => {
    const waist = Number(form.waist.replace(',', '.'))
    if (!waist || waist < 40 || waist > 250) return
    const hip = Number(form.hip.replace(',', '.'))
    save.mutate({
      date: todayIso(),
      waistCm: waist,
      hipCm: Number.isFinite(hip) && hip > 0 ? hip : null,
    })
    setForm({ waist: '', hip: '' })
  }

  return (
    <div className="card">
      <h2>Vücut kompozisyonu</h2>
      <Prose className="card-h">
        Kilo ne kadar verdiğini söyler, bu bölüm <strong>neyi</strong> verdiğini söyler.
        Ölçüm iki haftada bir yeterli — daha sık ölçmek gürültü ekler, bilgi eklemez.
      </Prose>

      <div className="grid g4" style={{ marginBottom: 16 }}>
        <div className="tile">
          <div className="lab">Vücut kitle indeksi</div>
          <div className="val">{n1(b)}</div>
          <div className="dl">{BMI_LABEL[cat]}</div>
        </div>

        {latest ? (
          <>
            <div className="tile">
              <div className="lab">Bel çevresi</div>
              <div className="val">{n1(latest.waistCm)}<span className="hero-u" style={{ fontSize: 15 }}> cm</span></div>
              <div className="dl">
                {previous
                  ? waistChangeIsReal(latest.waistCm - previous.waistCm)
                    ? `${latest.waistCm < previous.waistCm ? '−' : '+'}${n1(Math.abs(latest.waistCm - previous.waistCm))} cm`
                    : `değişim ${WAIST_NOISE_CM} cm altında — ölçüm hatasıyla karışır`
                  : `hedef ${Math.round(waistTarget(profile.heightCm))} cm`}
              </div>
            </div>

            <div className="tile">
              <div className="lab">Bel / boy oranı</div>
              <div className="val">{whtr(latest.waistCm, profile.heightCm).toFixed(2)}</div>
              <div className="dl">{WHTR_LABEL[whtrBand(whtr(latest.waistCm, profile.heightCm))]}</div>
            </div>

            <BodyFatTile
              waistCm={latest.waistCm} heightCm={profile.heightCm} sex={sex}
              weightKg={weightKg} targetWeightKg={profile.targetWeightKg} />
          </>
        ) : (
          <div className="tile" style={{ gridColumn: 'span 3' }}>
            <div className="lab">Bel çevresi</div>
            <div className="dl" style={{ marginTop: 6 }}>
              Henüz ölçüm yok. Bel çevresi, kiloya göre daha iyi bir sağlık göstergesi —
              ve kilo durduğu haftalarda bile ilerlemeyi gösterebiliyor.
            </div>
          </div>
        )}
      </div>

      <h3>Bugünün ölçümü</h3>
      <div className="row">
        <div>
          <label className="f">Bel (cm)</label>
          <input type="number" step="0.1" inputMode="decimal" placeholder="ör. 118.5"
                 value={form.waist} onChange={(e) => setForm({ ...form, waist: e.target.value })} />
        </div>
        <div>
          <label className="f">Kalça (cm) — isteğe bağlı</label>
          <input type="number" step="0.1" inputMode="decimal"
                 value={form.hip} onChange={(e) => setForm({ ...form, hip: e.target.value })} />
        </div>
        <button className="primary" onClick={submit} disabled={!form.waist || save.isPending}>
          Kaydet
        </button>
      </div>

      {list.length > 0 && (
        <>
          <h3>Ölçüm geçmişi</h3>
          <table>
            <thead>
              <tr><th>Tarih</th><th>Bel</th><th>Değişim</th><th>Bel/boy</th><th></th></tr>
            </thead>
            <tbody>
              {[...list].reverse().slice(0, 12).map((row, i, arr) => {
                const prev = arr[i + 1]
                const delta = prev ? row.waistCm - prev.waistCm : null
                return (
                  <tr key={row.date}>
                    <td>{trDate(row.date)}</td>
                    <td>{n1(row.waistCm)} cm</td>
                    <td style={{ color: delta !== null && waistChangeIsReal(delta)
                        ? (delta < 0 ? 'var(--good-text)' : 'var(--s2)') : 'var(--muted)' }}>
                      {delta === null
                        ? '—'
                        : waistChangeIsReal(delta)
                          ? `${delta < 0 ? '−' : '+'}${n1(Math.abs(delta))}`
                          : 'gürültü içinde'}
                    </td>
                    <td>{whtr(row.waistCm, profile.heightCm).toFixed(2)}</td>
                    <td className="act">
                      <button onClick={() => del.mutate(row.date)}>Sil</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {list.length > 1 && (() => {
            const total = latest.waistCm - list[0].waistCm
            // Tek tek adımlar gürültünün altında kalabilir ama toplamları gerçek
            // olabilir. Sadece adım farkını göstermek "hiçbir şey olmuyor" izlenimi
            // veriyordu — asıl ilerleme burada görünüyor.
            return (
              <p className={waistChangeIsReal(total) ? 'sub' : 'note'} style={{ marginTop: 10 }}>
                <strong>
                  İlk ölçümden bu yana {total < 0 ? '−' : '+'}{n1(Math.abs(total))} cm
                </strong>
                {' '}({trDate(list[0].date)} → {trDate(latest.date)})
                {waistChangeIsReal(total)
                  ? total < 0
                    ? ' — bu gerçek bir değişim, ölçüm hatası değil.'
                    : ' — ters yönde ve ölçüm hatasının üstünde.'
                  : ' — henüz ölçüm hatasının içinde, birkaç ölçüm daha gerek.'}
              </p>
            )
          })()}
          <Prose className="note">
            Değişim sütunu ardışık iki ölçüm arasındaki farkı gösteriyor ve
            {' '}{WAIST_NOISE_CM} cm altını "gürültü içinde" diye işaretliyor: üç ölçümün
            ortalamasında bile pratik hata ±1–1.5 cm. Tek adım gürültüde kalsa da
            toplamı anlamlı olabilir — üstteki satır onu söylüyor.
          </Prose>
        </>
      )}

      <details style={{ marginTop: 14 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13.5, color: 'var(--ink-2)' }}>
          Nasıl ölçülür — protokole uymazsan sayı işe yaramaz
        </summary>
        <ol className="tight" style={{ fontSize: 13.5, marginTop: 8 }}>
          <li>Sabah, aç karnına, tuvaletten sonra — tartıyla aynı anda.</li>
          <li>Ayakta dik dur, ayaklar 25–30 cm açık, kollar yanda serbest.</li>
          <li><strong>Göbek deliği hizasından</strong>, mezura yere paralel. Aynada kontrol et.</li>
          <li>Normal nefes verişin sonunda oku. Karnı içeri çekme, nefes tutma.</li>
          <li>Mezura teni sıkmadan gergin — deride iz bırakmasın.</li>
          <li><strong>Üç kez ölç, ortalamasını gir.</strong></li>
        </ol>
        <Prose className="note">
          Boyun ölçümü istemiyoruz: kendi kendine ölçümde uyum sınırı ±5 cm, yani
          12 haftalık gerçek değişimden (1–2 cm) büyük. Sadece bel + boy kullanan
          bir formül (RFM) hem daha az sürtünme hem daha az gürültü.
        </Prose>
      </details>
    </div>
  )
}

function BodyFatTile({ waistCm, heightCm, sex, weightKg, targetWeightKg }: {
  waistCm: number; heightCm: number; sex: Sex; weightKg: number; targetWeightKg: number
}) {
  const value = rfm(waistCm, heightCm, sex)
  const range = rfmRange(value)
  const lean = leanMassKg(weightKg, value)
  const projected = projectBodyFat(weightKg, value, targetWeightKg, 0.15)
  const benefit = trainingBenefit(weightKg, value, targetWeightKg)

  return (
    <div className="tile">
      <div className="lab">Yağ oranı — tahmin</div>
      <div className="val">≈{n1(value)}<span className="hero-u" style={{ fontSize: 15 }}>%</span></div>
      <div className="dl">
        {BODY_FAT_LABEL[bodyFatBand(value, sex)]} · aralık %{Math.round(range.low)}–{Math.round(range.high)}
      </div>
      <div className="dl" style={{ marginTop: 6 }}>
        Yağsız kütle ≈ {n1(lean)} kg
      </div>
      <div className="dl" style={{ marginTop: 6, color: 'var(--ink-2)' }}>
        {Math.round(targetWeightKg)} kg'da beklenen: ≈%{n1(projected)}
        {benefit > 0.5 && ` — antrenmansız ${n1(benefit)} puan daha yüksek olurdu`}
      </div>
      <Prose className="note" style={{ marginTop: 8, fontSize: 11.5 }}>
        Bu bir tahmin, ölçüm değil. Mezurayla yapılan en iyi tahminin bile hata payı
        ±{RFM_UNCERTAINTY} puan. Mutlak değere değil, aylar içindeki değişime bak.
      </Prose>
    </div>
  )
}
